import { alpha } from '@mui/material/styles'
import type { SystemStyleObject, Theme } from '@mui/system'

/**
 * The local-control PQ surface's motion vocabulary: a small, deliberate set of
 * state-driven cues, never decoration. Each helper collapses to no animation
 * under `prefers-reduced-motion`, matching how the rest of the app behaves
 * (see `HoldToActionButton`, `NavBar`).
 *
 * Four primitives, each bound to a single meaning:
 *  - `pulseSx`     — a running marker is live right now (the market accordion's bolt).
 *  - `breatheSx`   — an in-flight run on a small phase dot: alive but unobtrusive.
 *  - `settleInSx`  — a value has just arrived at a good resting state.
 *  - `glowSweepSx` — a market just qualified across every resource (plays once).
 *
 * The curve is ease-out-quint per the design language: confident arrival, no
 * bounce, no elastic.
 *
 * Typed as `SystemStyleObject` (not the broad `SxProps`) so they compose cleanly
 * as entries in an array `sx`, e.g. `sx={[base, isRunning && breatheSx]}`.
 */

const REDUCE = '@media (prefers-reduced-motion: reduce)'

const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)'

/** Opacity breath for a "running" marker (the market accordion's bolt). */
export const pulseSx: SystemStyleObject<Theme> = {
  animation: 'pq-pulse 1.6s ease-in-out infinite',
  '@keyframes pq-pulse': {
    '0%, 100%': { opacity: 0.45 },
    '50%': { opacity: 1 },
  },
  [REDUCE]: { animation: 'none', opacity: 1 },
}

/** A small phase dot that is mid-run: gently breathing in size and opacity. */
export const breatheSx: SystemStyleObject<Theme> = {
  animation: 'pq-breathe 1.8s ease-in-out infinite',
  '@keyframes pq-breathe': {
    '0%, 100%': { opacity: 0.5, transform: 'scale(0.78)' },
    '50%': { opacity: 1, transform: 'scale(1)' },
  },
  [REDUCE]: { animation: 'none', opacity: 1, transform: 'none' },
}

/** A one-shot scale-and-fade in for a value that just landed somewhere good. */
export const settleInSx: SystemStyleObject<Theme> = {
  animation: `pq-settle 360ms ${EASE_OUT}`,
  '@keyframes pq-settle': {
    from: { transform: 'scale(0.4)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },
  [REDUCE]: { animation: 'none' },
}

/**
 * A soft success-tinted light sweeping once across a surface the moment a market
 * fully qualifies, then gone. Animates only `background-position`, so it never
 * touches layout.
 */
export const glowSweepSx = (theme: Theme): SystemStyleObject<Theme> => ({
  backgroundImage: `linear-gradient(100deg, transparent 30%, ${alpha(
    theme.palette.success.main,
    0.16,
  )} 50%, transparent 70%)`,
  backgroundSize: '220% 100%',
  backgroundRepeat: 'no-repeat',
  animation: 'pq-glow-sweep 1200ms ease-out',
  '@keyframes pq-glow-sweep': {
    from: { backgroundPosition: '170% 0' },
    to: { backgroundPosition: '-70% 0' },
  },
  [REDUCE]: { animation: 'none', backgroundImage: 'none' },
})
